import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Handshake } from 'lucide-react';
import { usePermissions } from '../../hooks/usePermissions';
import { ReferralPartnersPage } from './ReferralPartners';
import { ReferralPartnersTrash } from './ReferralPartnersTrash';

/**
 * Referrals workspace — the single source for the referral-partner registry
 * (and its trash). Payouts moved to the unified Finance → Payouts workspace, so
 * any legacy `?tab=payouts|vouchers` link is forwarded there.
 */
export function Referrals() {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasAccess } = usePermissions();

  const canPartners = hasAccess('/referrals/registry');
  const canTrash = hasAccess('/referrals/trash');

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const requested = params.get('tab');

  // Legacy deep links from when payouts lived here.
  const isLegacyPayoutTab = requested === 'payouts' || requested === 'vouchers';
  useEffect(() => {
    if (isLegacyPayoutTab) navigate('/billing/payouts?tab=outstanding', { replace: true });
  }, [isLegacyPayoutTab, navigate]);

  if (isLegacyPayoutTab) return null;

  // The trash view is reached from the "Trash" button in the partners header.
  if (requested === 'trash' && canTrash) return <ReferralPartnersTrash />;

  const header = (
    <div className="flex items-start gap-3">
      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
        <Handshake size={20} className="text-primary" />
      </div>
      <div>
        <h1 className="text-xl font-bold text-text">Referrals</h1>
        <p className="text-sm text-text-muted mt-0.5">
          Referral partners — internal employees, doctors and outside referrers.
        </p>
      </div>
    </div>
  );

  if (!canPartners) {
    return (
      <div className="space-y-5">
        {header}
        <div className="rounded-xl border border-border bg-surface px-4 py-10 text-center text-sm text-text-muted">
          You do not have access to any referral views. Ask an administrator to grant
          <span className="font-medium text-text"> referrals </span>
          permissions.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <ReferralPartnersPage
        embedded
        heading="Referrals"
        subheading="Referral partners — internal employees, doctors and outside referrers."
      />
    </div>
  );
}
